import React, {Component, ReactNode} from 'react'
import {BlockSpacer} from '../../../styledComponents/ResponsiveConvenience'
import {FlexGrowContent, RowFlexContainer} from '../../../styledComponents/FlexComponents'
import {Close} from '@mui/icons-material'
import {Colors, FontSize, FontWeights} from '../../../styledComponents/Constants'
import moment, {Moment} from 'moment'
import ReserveAvailabilityDuration from '../ReserveAvailabilityDuration'
import {FACILITY_RESERVATION_CLASSIFICATION, RESERVATION_STATUS} from '../../../CodeMaster'
import {convertToMoment} from '../helpers/helpers'
import {DialogCloseButton, DialogTitle, DialogTitleArea, DisplayItemDividerDialog} from '../components/CommonStyle'
import styled from 'styled-components'
import {
    FacilityName,
    MainArea,
    PanelArea,
    ReservationDetailTD,
    ReservationDurationTD,
    TimeSlot,
    TimeSlotTable,
    TimeSlotTR,
} from './RFRSReservationStatusDetailComponent'
import {Nullable} from '../../../app/TypeHelpers'
import {NavigateFunction} from 'react-router-dom'

interface Props {
    condominiumCd: string
    facilityId: number
    facilityName: string
    reservationId: Nullable<number>
    searchDate: Moment
    onCancel: () => void
    navigate: NavigateFunction

    reserveAvailabilityDuration: ReserveAvailabilityDuration
    children?: ReactNode
}

class RFRSReservationStatusDetailForDurationComponent extends Component<Props> {
    constructor(props: Readonly<Props>) {
        super(props)
    }

    splitAvailableTimeSlot(
        availableTimeSlotList: TimeSlot[],
        currentTimeSlotList: TimeSlot[],
    ): TimeSlot[] {
        const beforeLotteryTimeSlotList: TimeSlot[] = currentTimeSlotList
            .filter(currentReserve => currentReserve.reservationStatus === RESERVATION_STATUS.BEFORE_LOTTERY)
            .sort((a, b) => {
                return a.startDateTime.unix() - b.startDateTime.unix()
            })

        let availableGroups: { available: TimeSlot, beforeLotteryList: TimeSlot[] }[] = []
        availableTimeSlotList.forEach(availableTimeSlot => {
            const beforeLotteryList = beforeLotteryTimeSlotList.filter(lotteryTimeSlot => {
                return availableTimeSlot.startDateTime.isSameOrBefore(lotteryTimeSlot.startDateTime) &&
                    availableTimeSlot.endDateTime.isSameOrAfter(lotteryTimeSlot.endDateTime)
            })
            availableGroups = [...availableGroups, {
                available: availableTimeSlot,
                beforeLotteryList: beforeLotteryList,
            }]
        })

        let splitTimeSlotList: TimeSlot[] = []
        availableGroups.forEach(group => {
            let availableStartDateTime = group.available.startDateTime
            group.beforeLotteryList.forEach(beforeLottery => {
                if (!availableStartDateTime.isSame(beforeLottery.startDateTime)) {
                    splitTimeSlotList = [...splitTimeSlotList, {
                        ...group.available,
                        startDateTime: availableStartDateTime,
                        endDateTime: beforeLottery.startDateTime,
                    }]
                }
                availableStartDateTime = beforeLottery.endDateTime
            })
            if (!availableStartDateTime.isSame(group.available.endDateTime)) {
                splitTimeSlotList = [...splitTimeSlotList, {
                    ...group.available,
                    startDateTime: availableStartDateTime,
                }]
            }
        })

        return splitTimeSlotList
    }

    createTimeSlot(): TimeSlot[] {
        const {reserveAvailabilityDuration, reservationId} = this.props
        const {reserveAvailableList, currentReserves, otherReserves} = reserveAvailabilityDuration

        const availableTimeSlotList: TimeSlot[] = reserveAvailableList.map(reserveAvailable => {
            return {
                type: 'blank',
                startDateTime: convertToMoment(reserveAvailable.startDate, reserveAvailable.startTime),
                endDateTime: convertToMoment(reserveAvailable.endDate, reserveAvailable.endTime),
                reservationStatus: null,
                ownerName: null,
                title: null,
            }
        })

        const currentTimeSlotList: TimeSlot[] = currentReserves
            .filter(currentReserve => (currentReserve.id !== reservationId))
            .map(currentReserve => {
                return {
                    type: 'current',
                    startDateTime: moment(currentReserve.startDateTime),
                    endDateTime: moment(currentReserve.endDateTime),
                    reservationStatus: currentReserve.status,
                    ownerName: currentReserve.ownerName === null ? 'あなた' : `${currentReserve.ownerName}さん`,
                    title: currentReserve.title,
                }
            })

        const otherTimeSlotList: TimeSlot[] = otherReserves.map(otherReserve => {
            return {
                type: 'other',
                startDateTime: moment(otherReserve.startDateTime),
                endDateTime: moment(otherReserve.endDateTime),
                reservationStatus: null,
                ownerName: otherReserve.classification === FACILITY_RESERVATION_CLASSIFICATION.ADMIN ? '管理者' : '他の人',
                title: otherReserve.title,
            }
        })

        let timeSlotList: TimeSlot[] = this.splitAvailableTimeSlot(availableTimeSlotList, currentTimeSlotList)
        timeSlotList = timeSlotList.concat(currentTimeSlotList).concat(otherTimeSlotList)
        if (timeSlotList.length === 0) {
            return []
        }
        timeSlotList.sort((a, b) => {
            return a.startDateTime.unix() - b.startDateTime.unix()
        })

        return timeSlotList
    }

    renderStatusTable(): ReactNode {
        const {searchDate} = this.props
        const timeSlotList: TimeSlot[] = this.createTimeSlot()
        const hasBlank = timeSlotList.some(timeSlot => timeSlot.type === 'blank')
        return (<>
            {
                !hasBlank && <>
                    <Message>{searchDate.format('M月D日(ddd)')}は予約できません。</Message>
                    <BlockSpacer height={'10px'}/>
                </>
            }
            {
                timeSlotList.length !== 0 && <>
                    <Note>この施設の予約情報と空き時間を確認できます。</Note>
                    <BlockSpacer height={'10px'}/>
                    <TimeSlotTable>
                        <tbody>
                        {timeSlotList.map((timeSlot, index) =>
                            this.renderTimeSlot(timeSlot, index, timeSlotList.length)
                        )}
                        </tbody>
                    </TimeSlotTable>
                </>
            }
        </>)
    }

    renderTimeSlot(
        timeSlot: TimeSlot,
        index: number,
        numOfTimeSlots: number,
    ): ReactNode {
        const {searchDate} = this.props
        let startDate: string = ''
        const startTime: string = timeSlot.startDateTime.format('HH:mm')
        let endDate: string = ''
        let endTime: string = timeSlot.endDateTime.format('HH:mm')
        let startIsOutOfDuration: boolean = false
        let endIsOutOfDuration: boolean = false
        if (index === 0) {
            startDate = timeSlot.startDateTime.format('M/D')
            if (!searchDate.isSame(timeSlot.startDateTime, 'day')) {
                endDate = timeSlot.endDateTime.format('M/D')
                startIsOutOfDuration = true
            }
        }
        if (index === numOfTimeSlots - 1) {
            if (timeSlot.type === 'blank') {
                if (endTime === '00:00') {
                    endTime = '24:00'
                }
            } else {
                if (!searchDate.isSame(timeSlot.endDateTime, 'day')) {
                    endDate = timeSlot.endDateTime.format('M/D')
                    endIsOutOfDuration = true
                }
            }
        }

        return (
            <TimeSlotTR key={index} type={timeSlot.type}>
                <ReservationDurationTD
                    startDate={startDate}
                    startTime={startTime}
                    startIsOutOfDuration={startIsOutOfDuration}
                    endDate={endDate}
                    endTime={endTime}
                    endIsOutOfDuration={endIsOutOfDuration}
                />
                <ReservationDetailTD timeSlot={timeSlot}/>
            </TimeSlotTR>
        )
    }

    render(): ReactNode {
        const {facilityName} = this.props

        return (<>
            <PanelArea>
                <DialogTitleArea>
                    <RowFlexContainer>
                        <DialogTitle>予約状況詳細</DialogTitle>
                        <DialogCloseButton onClick={this.props.onCancel} id={'close'}>
                            <Close fontSize={'large'} style={{color: `${Colors.AccentDark}`}}/>
                        </DialogCloseButton>
                    </RowFlexContainer>
                </DialogTitleArea>
                <DisplayItemDividerDialog/>
                <MainArea>
                    <BlockSpacer height={'14px'}/>
                    <FacilityName>{facilityName}</FacilityName>
                    <BlockSpacer height={'10px'}/>
                    {this.renderStatusTable()}
                </MainArea>
                <FlexGrowContent/>
            </PanelArea>
        </>)
    }
}

export default RFRSReservationStatusDetailForDurationComponent

const Message = styled.div`
  font-size: ${FontSize.Body};
  font-weight: ${FontWeights.Normal};
  line-height: 24px;
  color: ${Colors.AccentDark};
`

const Note = styled.div`
  font-size: ${FontSize.Small};
  font-weight: ${FontWeights.Normal};
  line-height: 18px;
  color: ${Colors.AccentDark};
`
